import type { ComponentProps } from 'react'
import { cn } from '@/lib/utils'

/**
 * A panel. ADR-0025 §3: **elevation is hairline, not shadow.**
 *
 * `--e-0` is a 1px rule and that is the whole of it — only the add sheet and the toast cast a
 * shadow, because they are the only two things temporarily on top of your life. A card with a
 * drop shadow would claim to be floating above a screen it is actually part of.
 *
 * `tone` picks the ground rather than the border, and the three status tints are how an inline alert
 * gets its colour without becoming a modal.
 */
export type CardProps = ComponentProps<'div'> & {
  tone?: 'raised' | 'sunken' | 'ok' | 'soon' | 'late'
  /** A dashed border — an empty state inviting a first action, not a filled panel. */
  dashed?: boolean
}

const TONE: Record<NonNullable<CardProps['tone']>, string> = {
  raised: 'bg-raised',
  sunken: 'bg-sunken',
  ok: 'bg-status-ok-bg',
  soon: 'bg-status-soon-bg',
  late: 'bg-status-late-bg',
}

export function Card({ className, tone = 'raised', dashed = false, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-3 text-ink',
        TONE[tone],
        dashed ? 'border border-dashed border-rule-2' : 'border border-rule-2',
        className,
      )}
      {...props}
    />
  )
}

/**
 * **`CardHeader` / `CardTitle` / `CardDescription` / `CardContent` / `CardFooter` are deliberately
 * absent.** They came with the shadcn scaffold and were never used by a single screen — every card
 * here composes its own header and padding, because the Ledger's cards are not uniform enough for a
 * five-part slot API to earn its place. Reintroducing them means finding two screens that want the
 * *same* shape first.
 */
